/**
 * App sidebar: RBAC menus grouped by label, with route-driven group open state.
 */

import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLayoutStore } from '@/stores/layoutStore';
import { useMode } from '@/contexts/ModeContext';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { useTheme } from '@/hooks/useTheme';
import { MenuItem } from './MenuItem';
import { ThemeToggle } from './ThemeToggle';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { InterfaceElement } from '@/types/interface-elements.types';
import * as Icons from 'lucide-react';
import { type LucideIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/** Sidebar group: label + icon + ordered items from `interface_elements`. */
interface GroupedMenus {
  groupLabel: string;
  groupIcon: string | null;
  groupOrder: number;
  items: InterfaceElement[];
}

/** RBAC sidebar: grouped menus, route-driven accordion, branding logo, footer actions. */
export const Sidebar: React.FC = () => {
  const { isSidebarCollapsed, sidebarWidth, toggleSidebar, openMenusByMode, setOpenMenus } =
    useLayoutStore();
  const { mode } = useMode();
  const activeMode = mode ?? 'in_person';
  const openMenus = openMenusByMode[activeMode] ?? [];
  const { elements: sidebarElements } = useInterfaceElements('sidebar');
  const location = useLocation();
  const { logos, isDarkMode, companyName, logoAlt } = useTheme();
  const displayName = companyName ?? '';
  const displayInitial = displayName.charAt(0).toUpperCase();

  /** Accordion: opening one group closes the others (per mode). */
  const toggleGroup = (groupLabel: string) => {
    if (openMenus.includes(groupLabel)) {
      setOpenMenus([], activeMode);
    } else {
      setOpenMenus([groupLabel], activeMode);
    }
  };

  /** Split sidebar elements into ungrouped items, accordion groups, and footer (`order >= 100`). */
  const { ungrouped, groups, footerItems } = React.useMemo(() => {
    const ungroupedItems: InterfaceElement[] = [];
    const footerItems: InterfaceElement[] = [];
    const groupsMap = new Map<string, GroupedMenus>();

    sidebarElements?.forEach((el) => {
      const order = el.userOrder ?? el.order ?? null;

      if (el.groupLabel) {
        if (!groupsMap.has(el.groupLabel)) {
          groupsMap.set(el.groupLabel, {
            groupLabel: el.groupLabel,
            groupIcon: el.groupIcon,
            groupOrder: el.groupOrder || 0,
            items: [],
          });
        }
        groupsMap.get(el.groupLabel)!.items.push(el);
      } else if (order >= 100) {
        footerItems.push(el);
      } else {
        ungroupedItems.push(el);
      }
    });

    const sortedGroups = Array.from(groupsMap.values()).sort((a, b) => a.groupOrder - b.groupOrder);

    sortedGroups.forEach((group) => {
      group.items.sort((a, b) => {
        const orderA = a.userOrder ?? a.order ?? Infinity;
        const orderB = b.userOrder ?? b.order ?? Infinity;
        return orderA - orderB;
      });
    });

    ungroupedItems.sort((a, b) => {
      const orderA = a.userOrder ?? a.order ?? Infinity;
      const orderB = b.userOrder ?? b.order ?? Infinity;
      return orderA - orderB;
    });

    footerItems.sort((a, b) => {
      const orderA = a.userOrder ?? a.order ?? Infinity;
      const orderB = b.userOrder ?? b.order ?? Infinity;
      return orderA - orderB;
    });

    return { ungrouped: ungroupedItems, groups: sortedGroups, footerItems };
  }, [sidebarElements]);

  /** Compact mark (`logo2`) when collapsed; full wordmark (`logo1`) otherwise, with theme fallback. */
  const currentLogo = React.useMemo(() => {
    const mode = isDarkMode ? 'dark' : 'light';
    const altMode = isDarkMode ? 'light' : 'dark';

    if (isSidebarCollapsed) {
      return (
        logos.logo2?.[mode] ||
        logos.logo2?.[altMode] ||
        logos.logo1?.[mode] ||
        logos.logo1?.[altMode] ||
        ''
      );
    }
    return logos.logo1?.[mode] || logos.logo1?.[altMode] || '';
  }, [isSidebarCollapsed, isDarkMode, logos]);

  const [imageError, setImageError] = React.useState(false);

  /** Clear the broken-image fallback when the logo URL changes. */
  React.useEffect(() => {
    setImageError(false);
  }, [currentLogo]);

  /**
   * Auto-open the sidebar group whose item matches the current route.
   * Runs on every pathname change (e.g. programmatic navigate from a data-table button).
   */
  React.useEffect(() => {
    const currentPath = location.pathname;

    const matchingGroup = groups.find((group) =>
      group.items.some((item) => {
        const routePath = item.config?.routePath as string | undefined;
        return routePath && currentPath === routePath;
      }),
    );

    if (!matchingGroup) return;

    if (!openMenus.includes(matchingGroup.groupLabel)) {
      setOpenMenus([matchingGroup.groupLabel], activeMode);
    }
  }, [location.pathname, groups, activeMode]);

  /** Lucide namespace as a string → component map (group icons are stored as names). */
  const IconsMap = Icons as unknown as Record<string, LucideIcon>;

  return (
    <aside
      className={cn('sidebar-container', isSidebarCollapsed && 'sidebar-collapsed')}
      style={{ '--sidebar-width': `${sidebarWidth}px` } as React.CSSProperties}
    >
      <button
        className="sidebar-toggle-button"
        onClick={toggleSidebar}
        aria-label={isSidebarCollapsed ? 'Ouvrir le menu' : 'Fermer le menu'}
      >
        {isSidebarCollapsed ? (
          <ChevronRight className="h-4 w-4" />
        ) : (
          <ChevronLeft className="h-4 w-4" />
        )}
      </button>

      <div className="sidebar-header">
        <Link to="/" className="flex items-center justify-center">
          {currentLogo && !imageError ? (
            <img
              src={currentLogo}
              alt={logoAlt}
              className={cn(
                'sidebar-logo',
                isSidebarCollapsed ? 'sidebar-logo-collapsed' : 'sidebar-logo-expanded',
              )}
              onError={() => setImageError(true)}
              onLoad={() => setImageError(false)}
            />
          ) : (
            <div
              className={cn(
                'flex items-center justify-center font-bold',
                isSidebarCollapsed ? 'w-10 h-10 text-lg' : 'h-10 px-4 text-xl',
                'bg-gradient-to-br from-[var(--color-2-nuance-1)] to-[var(--color-2-nuance-2)]',
                'text-white rounded-xl shadow-md',
                'border border-[var(--color-2-nuance-2)]',
              )}
            >
              {isSidebarCollapsed ? displayInitial : displayName}
            </div>
          )}
        </Link>
      </div>

      <nav className="sidebar-nav">
        {ungrouped.map((menu) => (
          <MenuItem key={menu.id} menu={menu} collapsed={isSidebarCollapsed} />
        ))}

        {ungrouped.length > 0 && groups.length > 0 && <div className="sidebar-divider" />}

        {groups.map((group) => {
          const isOpen = openMenus.includes(group.groupLabel);
          const GroupIcon = IconsMap[group.groupIcon || 'Folder'] || Icons.Folder;

          const groupButton = (
            <button
              type="button"
              className={cn('sidebar-group-header', isOpen && 'sidebar-group-header-open')}
              onClick={() => toggleGroup(group.groupLabel)}
            >
              <GroupIcon className="sidebar-group-icon" size={20} />
              {!isSidebarCollapsed && (
                <>
                  <span className="flex-1 text-left font-semibold">{group.groupLabel}</span>
                  <Icons.ChevronDown
                    className={cn('sidebar-group-chevron', isOpen && 'sidebar-group-chevron-open')}
                    size={18}
                  />
                </>
              )}
            </button>
          );

          return (
            <div key={group.groupLabel} className="sidebar-group">
              {isSidebarCollapsed ? (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>{groupButton}</TooltipTrigger>
                    <TooltipContent side="right" sideOffset={8}>
                      <p className="font-medium">{group.groupLabel}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                groupButton
              )}

              {!isSidebarCollapsed && isOpen && (
                <div className="sidebar-group-items">
                  {group.items.map((item) => (
                    <MenuItem key={item.id} menu={item} collapsed={false} isInGroup={true} />
                  ))}
                </div>
              )}

              {isSidebarCollapsed && isOpen && (
                <div className="sidebar-group-items">
                  {group.items.map((item) => (
                    <MenuItem key={item.id} menu={item} collapsed={true} isInGroup={true} />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {ungrouped.length === 0 && groups.length === 0 && (
          <div className={cn('px-4 py-2 text-center', 'text-muted-foreground text-sm')}>
            {isSidebarCollapsed ? '' : 'Aucun menu'}
          </div>
        )}
      </nav>

      <div className="sidebar-footer">
        {footerItems.map((menu) => (
          <MenuItem key={menu.id} menu={menu} collapsed={isSidebarCollapsed} />
        ))}

        <ThemeToggle collapsed={isSidebarCollapsed} />
      </div>
    </aside>
  );
};
